/** Single source of truth for parsing message attachments from raw message data.
 *
 *  Relocated from `tools/conversations/` (a `tools/` module may be imported only by a flow leaf, and the sole consumer
 *  is a components messaging client). The JSON parse reaches `parseJsonSafe` -- the const-homed core that
 *  `utils/json/safe-json`'s `safeJsonParse` itself delegates to, with the identical single-argument contract
 *  (`fallback = null`, large integers preserved) -- rather than that utils sibling, which the utils import floor
 *  (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`) forbids. */

import { parseJsonSafe } from '@federa/const/json-parse';
import { isPlainObject } from '@federa/const/record-guards';
import { isPresentValue } from '@federa/const/invalid-string-tokens';
import { firstPresent } from '@federa/const/dual-spelling';

interface ParsedAttachment {
	url: string;
	name?: string;
	type?: string;
}

/** Raw message shape for attachment extraction; supports both snake_case and camelCase field names. */
interface RawMessageWithFileIds {
	file_ids?: unknown;
	fileIds?: unknown;
}

/** Normalize a file-ids array into attachment objects; handles both string URLs and pre-formed attachment objects. */
function parseFileIdsToAttachments(fileIds: unknown[]): ParsedAttachment[] {
	return fileIds
		.map((item) => {
			if (typeof item === 'string') {
				const urlParts = item.split('/');
				const filename = urlParts.at(-1);
				return { url: item, name: filename };
			}
			return item;
		})
		.filter((item): item is ParsedAttachment => isPlainObject(item) && typeof item.url === 'string');
}

/** Parse attachments from a raw message's file_ids/fileIds field, handling JSON-string parsing and array normalization. */
export function parseAttachmentsFromRaw(raw: RawMessageWithFileIds): ParsedAttachment[] {
	const fileIdsRaw = firstPresent(raw, 'file_ids', 'fileIds');
	if (!isPresentValue(fileIdsRaw)) {
		return [];
	}

	let fileIds: unknown = fileIdsRaw;
	if (typeof fileIdsRaw === 'string') {
		// Try to parse as JSON array, fall back to single file ID
		fileIds = parseJsonSafe(fileIdsRaw) ?? [fileIdsRaw];
	}

	if (Array.isArray(fileIds)) {
		return parseFileIdsToAttachments(fileIds);
	}

	return [];
}
